/**
 * Momentum Rule Engine — Evaluate rules against entity/list data to produce signals
 *
 * Rules use simple template expressions (not arbitrary JS eval).
 * The engine matches page patterns, evaluates conditions with a safe
 * property-access evaluator, and produces MomentumSignal objects.
 */

import type {
  MomentumRule,
  MomentumSignal,
  MomentumConfig,
  MomentumThresholds,
} from './types';
import { DEFAULT_RULES } from './default-rules';

// ─── Default thresholds ──────────────────────────────────────────────────────

export const DEFAULT_THRESHOLDS: MomentumThresholds = {
  invoiceDelayWarnHours: 24,
  invoiceDelayCriticalHours: 72,
  slaRiskWindowHours: 4,
  arOverdueWarnDays: 30,
  arOverdueCriticalDays: 60,
  capacityWarnPct: 85,
  customerDeclinePct: 30,
  dsoTargetDays: 35,
};

// ─── Config merging ──────────────────────────────────────────────────────────

export function mergeConfig(tenantConfig?: Partial<MomentumConfig>): {
  rules: MomentumRule[];
  thresholds: MomentumThresholds;
} {
  const disabled = new Set(tenantConfig?.disabledRules ?? []);
  const base = DEFAULT_RULES.filter((r) => !disabled.has(r.id));
  const extra = tenantConfig?.extraRules ?? [];
  const rules = [...base, ...extra].sort((a, b) => a.priority - b.priority);
  const thresholds = { ...DEFAULT_THRESHOLDS, ...tenantConfig?.thresholds };
  return { rules, thresholds };
}

// ─── Glob pattern matching ───────────────────────────────────────────────────

function matchPagePattern(pattern: string, pageKey: string): boolean {
  // Support comma-separated patterns: "*.jobs*,*.dispatch*"
  const patterns = pattern.split(',').map((p) => p.trim());
  return patterns.some((p) => {
    const regex = new RegExp(
      '^' + p.replace(/\./g, '\\.').replace(/\*/g, '.*') + '$',
    );
    return regex.test(pageKey);
  });
}

// ─── Safe expression evaluator ───────────────────────────────────────────────

/**
 * Evaluate a simple condition/expression against a context object.
 * Supports property access (entity.status), comparisons, logical operators,
 * and basic arithmetic. NOT arbitrary JS — keeps it safe.
 */
export function safeEval(
  expression: string,
  ctx: Record<string, unknown>,
): unknown {
  // Build a safe function with only the ctx properties as locals
  try {
    const keys = Object.keys(ctx);
    const values = keys.map((k) => ctx[k]);
    // eslint-disable-next-line @typescript-eslint/no-implied-eval
    const fn = new Function(...keys, `"use strict"; return (${expression});`);
    return fn(...values);
  } catch {
    return undefined;
  }
}

// ─── Template interpolation ──────────────────────────────────────────────────

function interpolate(
  template: string,
  ctx: Record<string, unknown>,
): string {
  return template.replace(/\{\{([^}]+)\}\}/g, (_, path: string) => {
    const parts = path.trim().split('.');
    let val: unknown = ctx;
    for (const p of parts) {
      if (val == null || typeof val !== 'object') return '';
      val = (val as Record<string, unknown>)[p];
    }
    return val != null ? String(val) : '';
  });
}

// ─── Main: evaluate rules ────────────────────────────────────────────────────

export interface EvalContext {
  /** Current entity data (for detail/edit pages) */
  entity?: Record<string, unknown>;
  /** List aggregate data (for list pages) */
  list?: Record<string, unknown>;
  /** Current page key */
  pageKey: string;
  /** Current time (ms) */
  now?: number;
}

export function evaluateRules(
  evalCtx: EvalContext,
  config?: Partial<MomentumConfig>,
): MomentumSignal[] {
  const { rules, thresholds } = mergeConfig(config);
  const now = evalCtx.now ?? Date.now();
  const signals: MomentumSignal[] = [];

  // Build the flat context accessible to expressions
  const entity = evalCtx.entity ?? {};
  const list = evalCtx.list ?? {};
  const completedAt = entity.completedAt
    ? new Date(entity.completedAt as string).getTime()
    : undefined;
  const slaAt = entity.slaDeadline
    ? new Date(entity.slaDeadline as string).getTime()
    : undefined;

  // Human-readable label: prefer jobNumber/job_number over raw UUID id
  const rawId = (entity.jobNumber ?? entity.job_number ?? entity.id ?? '') as string;
  const entityLabel = /^[0-9a-f]{8}-[0-9a-f]{4}/.test(rawId)
    ? rawId.slice(0, 8).toUpperCase()
    : rawId;

  const ctx: Record<string, unknown> = {
    entity,
    list,
    thresholds,
    pageKey: evalCtx.pageKey,
    now,
    entityLabel,
    hoursAgo: completedAt ? Math.round((now - completedAt) / 3_600_000) : 0,
    hoursRemaining: slaAt ? Math.round((slaAt - now) / 3_600_000) : 999,
    missingFields: Array.isArray(entity.missingCriticalFields)
      ? (entity.missingCriticalFields as string[]).join(', ')
      : '',
  };

  for (const rule of rules) {
    // Page pattern check
    if (!matchPagePattern(rule.pagePattern, evalCtx.pageKey)) continue;

    // Intent filter
    if (rule.intents && rule.intents.length > 0) {
      const intent = config?.intent ?? 'cash_velocity';
      if (!rule.intents.includes(intent)) continue;
    }

    // Condition evaluation
    let conditionResult: unknown;
    try {
      conditionResult = safeEval(rule.condition, ctx);
    } catch {
      continue;
    }
    if (!conditionResult) continue;

    // Compute monetary/time impact
    const monetaryImpact = rule.monetaryExpression
      ? Number(safeEval(rule.monetaryExpression, ctx)) || undefined
      : undefined;
    const timeImpact = rule.timeExpression
      ? Number(safeEval(rule.timeExpression, ctx)) || undefined
      : undefined;

    // Build suggested action
    const sa = rule.suggestedAction;
    const suggestedAction = sa
      ? {
          label: interpolate(sa.labelTemplate, ctx),
          labelZh: interpolate(sa.labelTemplateZh, ctx),
          route: sa.route ? interpolate(sa.route, ctx) : undefined,
          prompt: sa.prompt ? interpolate(sa.prompt, ctx) : undefined,
          apiAction: sa.apiAction,
          requireConfirmation: sa.requireConfirmation,
        }
      : undefined;

    signals.push({
      id: `${rule.id}:${(entity.id as string) ?? evalCtx.pageKey}`,
      type: rule.signalType,
      severity: rule.defaultSeverity,
      monetaryImpact,
      currency: monetaryImpact != null ? ((entity.currency as string) ?? 'SGD') : undefined,
      timeImpact,
      timeUnit: timeImpact != null ? (rule.timeExpression?.includes('days') ? 'days' : 'hours') : undefined,
      message: interpolate(rule.messageTemplate, ctx),
      messageZh: interpolate(rule.messageTemplateZh, ctx),
      suggestedAction,
      source: {
        pageKey: evalCtx.pageKey,
        entityType: entity.entityType as string | undefined,
        entityId: entity.id as string | undefined,
        ruleId: rule.id,
      },
      timestamp: new Date(now).toISOString(),
    });
  }

  return signals;
}

// ─── Batch evaluation for list pages ─────────────────────────────────────────

export function evaluateListSignals(
  entities: Record<string, unknown>[],
  pageKey: string,
  config?: Partial<MomentumConfig>,
): MomentumSignal[] {
  const signals: MomentumSignal[] = [];
  // Cap to first 100 entities for performance — momentum bar is a summary
  const capped = entities.length > 100 ? entities.slice(0, 100) : entities;
  for (const entity of capped) {
    signals.push(...evaluateRules({ entity, pageKey }, config));
  }
  return signals;
}
